import { useState } from 'react';
import { useInView } from '../../hooks/useInView';
import { Link } from 'react-router-dom';

const testimonials = [
  {
    quote: 'Transformational doesn\'t come close to describing it. I felt immediate, undeniable relief and a clarity I hadn\'t experienced in years. The level of personalization is unlike anything I have ever encountered — my practitioner understood what my body needed before I could even articulate it.',
    author: 'Sarah M.',
    location: 'Yardley, PA',
    service: 'Personalized Healing Session',
    rating: 5,
  },
  {
    quote: 'I\'ve been to many healers, therapists, and wellness practitioners over the years. This practice stands entirely alone. The precision, the intentional care, the caliber of experience — it\'s a completely different tier. My nervous system finally feels regulated for the first time in years.',
    author: 'Anjali K.',
    location: 'Newtown, PA',
    service: 'Trauma Release & Nervous System Reset',
    rating: 5,
  },
  {
    quote: 'I was skeptical going in. After two sessions, the chronic tension I\'d been managing for three years started to actually release — not mask, release. The approach goes far deeper than the surface. I came back for a third session and I\'m genuinely grateful I found this practice.',
    author: 'Thomas B.',
    location: 'Doylestown, PA',
    service: 'Holistic Healing Therapy',
    rating: 5,
  },
  {
    quote: 'Unlike anything I\'ve tried. Truly personalized, deeply effective. I came in holding years of stored emotional weight and left feeling like an entirely different person. The practitioner\'s presence alone communicates that you are in completely capable, caring hands.',
    author: 'James R.',
    location: 'New Hope, PA',
    service: 'Energy Work & Bodywork',
    rating: 5,
  },
  {
    quote: 'This is what healing is supposed to feel like. Not rushed, not generic, not surface-level. Every single element of my session was tailored to me. The post-session guidance alone was more thorough than anything I\'ve received from a conventional provider.',
    author: 'Maria L.',
    location: 'Langhorne, PA',
    service: 'Personalized Healing Session',
    rating: 5,
  },
];

interface TestimonialsSectionProps {
  onScrollTo?: (id: string) => void;
}

export default function TestimonialsSection({ onScrollTo }: TestimonialsSectionProps) {
  const [active, setActive] = useState(0);
  const { ref, inView } = useInView();
  const current = testimonials[active];

  const handleBook = () => {
    if (onScrollTo) {
      onScrollTo('booking');
    } else {
      const el = document.getElementById('booking');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="testimonials"
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#080808] py-32 relative overflow-hidden border-t border-[#d4af37]/8"
    >
      {/* Background glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[700px] h-[500px] rounded-full bg-[#d4af37]/[0.025] blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative z-10">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="w-8 h-px bg-[#d4af37]/30" />
              <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Client Experiences
              </p>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              What Clients Say
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActive(idx)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  idx === active ? 'w-8 h-2 bg-[#d4af37]' : 'w-2 h-2 bg-[#2a2a2a] hover:bg-[#d4af37]/40'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Large feature card */}
        <div
          className={`bg-[#0f0f0f] border border-[#1e1e1e] rounded-2xl p-10 md:p-16 mb-6 relative overflow-hidden transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '150ms' }}
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#d4af37]/0 via-[#d4af37]/60 to-[#d4af37]/0" />

          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="flex-1">
              <div className="flex gap-0.5 mb-8">
                {Array.from({ length: current.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d4af37] text-sm" />
                ))}
              </div>
              <span className="text-[#d4af37]/20 text-8xl font-light leading-none select-none" style={{ fontFamily: 'Cormorant Garamond, serif' }}>&ldquo;</span>
              <p
                className="text-white text-2xl md:text-3xl font-light leading-[1.65] -mt-6"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
              >
                {current.quote}
              </p>
            </div>
            <div className="lg:w-52 flex-shrink-0">
              <div className="bg-[#151515] border border-[#1e1e1e] rounded-xl p-6">
                <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center mb-4">
                  <i className="ri-user-line text-[#d4af37] text-lg" />
                </div>
                <p className="text-white text-base font-medium mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {current.author}
                </p>
                <p className="text-[#4a4a4a] text-xs mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {current.location}
                </p>
                <div className="h-px bg-[#1e1e1e] mb-3" />
                <p className="text-[#d4af37] text-xs italic" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {current.service}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mini cards row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-14">
          {testimonials.filter((_, i) => i !== active).slice(0, 3).map((t, idx) => (
            <div
              key={idx}
              onClick={() => setActive(testimonials.indexOf(t))}
              className="bg-[#0c0c0c] border border-[#181818] hover:border-[#d4af37]/20 rounded-xl p-5 cursor-pointer transition-all duration-300 group"
            >
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d4af37] text-[10px]" />
                ))}
              </div>
              <p className="text-[#6a6a6a] text-xs leading-relaxed line-clamp-3 group-hover:text-[#8a8a8a] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                &ldquo;{t.quote}&rdquo;
              </p>
              <p className="text-[#4a4a4a] text-xs mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>— {t.author}, {t.location}</p>
            </div>
          ))}
        </div>

        {/* Post-testimonial CTA */}
        <div className="text-center">
          <p className="text-[#5a5a5a] text-sm mb-5" style={{ fontFamily: 'Inter, sans-serif' }}>
            Ready to experience this for yourself?
          </p>
          <button
            onClick={handleBook}
            className="inline-flex items-center gap-2 bg-[#d4af37] hover:bg-[#c09b2a] text-[#080808] px-10 py-4 rounded-full text-xs uppercase tracking-[0.18em] font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Begin Your Session
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
